import gql from "graphql-tag";
import useModuleBuilderContentPermission from "@src/features/libraryItem/module/hooks/useModuleBuilderContentPermission";
import { BuilderActionContentIds } from "@src/features/libraryItem/contexts/BuilderActionContext";
import {
  ContentPermissionsFragment,
  useContentPermissionsQuery,
} from "@src/features/libraryItem/hooks/useContentPermissions.generated";
import { useMemo } from "react";
import useIsCatalogLibraryItemPage from "@src/features/libraryItem/hooks/useIsCatalogLibraryItemPage";

export const ALL_FALSE_CONTENT_PERMISSIONS: ContentPermissionsFragment = {
  hasEditContentPermission: false,
  hasPublishPermission: false,
  hasCommentPermission: false,
  hasShareWithIndividualsPermission: false,
  hasEditAdminAndManagerSharingPermission: false,
};

const useContentPermissions = (
  args: BuilderActionContentIds,
): ContentPermissionsFragment | undefined => {
  const moduleBuilderVersionPermissions = useModuleBuilderContentPermission();
  const { data } = useContentPermissionsQuery({
    variables: {
      courseId: args.courseId,
      moduleId: args.moduleId,
      skillId: args.skillId,
      trainingResourceId: args.trainingResourceId,
    },
    skip:
      !!moduleBuilderVersionPermissions ||
      (!args.courseId &&
        !args.moduleId &&
        !args.skillId &&
        !args.trainingResourceId),
    fetchPolicy: "cache-first",
  });
  const catalogPage = useIsCatalogLibraryItemPage();
  const result = useMemo(() => {
    const permissions =
      moduleBuilderVersionPermissions || data?.ContentPermissions || undefined;
    return permissions && catalogPage
      ? { ...permissions, hasCommentPermission: false }
      : permissions;
  }, [catalogPage, data?.ContentPermissions, moduleBuilderVersionPermissions]);
  return result;
};

export default useContentPermissions;

gql`
  query ContentPermissions(
    $courseId: Int
    $moduleId: Int
    $skillId: Int
    $trainingResourceId: UUID
  ) {
    ContentPermissions: AdminContentPermissions(
      courseId: $courseId
      moduleId: $moduleId
      skillId: $skillId
      trainingResourceId: $trainingResourceId
    ) {
      ...ContentPermissions
    }
  }

  fragment ContentPermissions on ContentPermissions {
    hasEditContentPermission
    hasPublishPermission
    hasCommentPermission
    hasShareWithIndividualsPermission
    hasEditAdminAndManagerSharingPermission
  }
`;
